import { uploadSessionRepository } from '~/repositories/uploadSessionRepository';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type { UploadFileOptions } from '~/repositories/uploadSessionRepository';
import type { EmailDto, EmailsDto, SendEmailJsonDto } from '~/swagger/portal/Api';

const EMAIL_URL = '/api/emails';

export type EmailListFilter = {
  ownerId?: string;
  ownerIds?: string[];
  tenantIds?: string[];
  leaseIds?: string[];
  vendorIds?: string[];
  subjectLike?: string;
  bodyLike?: string;
  disable?: boolean;
  page?: number;
  pageSize?: number;
  skip?: number;
};

export type EmailFilter = {
  page?: number;
  pageSize?: number;
  skip?: number;
  disable?: boolean;
};

export class EmailRepository extends BaseRestRepository {
  public async getEmails(filter?: EmailListFilter): Promise<EmailsDto> {
    const url = urlWithQuery(EMAIL_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getEmail(emailId: string): Promise<EmailDto> {
    const url = `${EMAIL_URL}/${emailId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getOwnerEmails(ownerId: string, filter?: EmailFilter): Promise<EmailsDto> {
    const url = urlWithQuery(`${EMAIL_URL}/owners/${ownerId}`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getTenantEmails(tenantId: string, filter?: EmailFilter): Promise<EmailsDto> {
    const url = urlWithQuery(`${EMAIL_URL}/tenants/${tenantId}`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getLeaseEmails(leaseId: string, filter?: EmailFilter): Promise<EmailsDto> {
    const url = urlWithQuery(`${EMAIL_URL}/leases/${leaseId}`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getVendorEmails(vendorId: string, filter?: EmailFilter): Promise<EmailsDto> {
    const url = urlWithQuery(`${EMAIL_URL}/vendors/${vendorId}`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async uploadEmailAttachments(files: File[], opts?: UploadFileOptions): Promise<string[]> {
    const sessionUrl = `${EMAIL_URL}/upload-sessions`;
    return await uploadSessionRepository.uploadFiles(sessionUrl, files, opts);
  }

  public async sendOwnerEmailJson(ownerId: string, payload: SendEmailJsonDto) {
    const url = `${EMAIL_URL}/owners/${ownerId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async sendTenantEmailJson(tenantId: string, payload: SendEmailJsonDto) {
    const url = `${EMAIL_URL}/tenants/${tenantId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async sendLeaseEmailJson(leaseId: string, payload: SendEmailJsonDto) {
    const url = `${EMAIL_URL}/leases/${leaseId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async sendVendorEmailJson(vendorId: string, payload: SendEmailJsonDto) {
    const url = `${EMAIL_URL}/vendors/${vendorId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }
}

export const emailRepository = new EmailRepository();

export const emailUploadMethod = async (file: File, options?: { abortSignal?: AbortSignal }) => {
  const [sessionId] = await emailRepository.uploadEmailAttachments([file], {
    abortSignal: options?.abortSignal,
    maxConcurrency: 1,
  });
  return sessionId;
};
